// orders-context.tsx
// Context for managing order table state, including filters, sorting, and pagination.
// Provides a React context and provider for use throughout the application.

import { Filter, Filters, SortOrder } from "@/types/order-table";
import {
  createContext,
  FC,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  ReactNode,
  useMemo,
  useCallback,
} from "react";

const noop = () => null;

const defaultValues = {
  sortCriteria: "lastUpdated",
  sortOrder: null,
  filters: {
    authStatus: ["Needs Info", "Pending", "Not Required", "Obtained", "Closed"],
    departments: [],
    providerNames: [],
    orderTypes: [],
    insurances: [],
    patientNames: [],
    priority: [],
    recentlyUpdated: [],
    scheduledStatus: [],
  },
  pageNumber: 1,
  updateFilters: noop,
  setPageNumber: noop,
  setSortOrder: noop,
  setSortCriteria: noop,
};

/**
 * Orders context value shape.
 */
interface Orders {
  sortCriteria?: string;
  sortOrder?: SortOrder;
  filters: Filters;
  pageNumber: number;
  updateFilters: (type: Filter, filters: string[]) => void;
  setPageNumber: Dispatch<SetStateAction<number>>;
  setSortOrder: Dispatch<SetStateAction<SortOrder>>;
  setSortCriteria: Dispatch<SetStateAction<string>>;
}

/**
 * OrdersContext provides state and actions for order table filtering, sorting, and pagination.
 */
const OrdersContext = createContext<Orders>(defaultValues);

/**
 * OrdersContextProvider wraps children with order table state context.
 * @param children - React children to provide context to
 */
export const OrdersContextProvider: FC<PropsWithChildren<ReactNode>> = ({
  children,
}) => {
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    defaultValues.sortOrder,
  );
  const [sortCriteria, setSortCriteria] = useState<string>(
    defaultValues.sortCriteria,
  );
  const [filters, setFilters] = useState<Filters>(defaultValues.filters);
  const [pageNumber, setPageNumber] = useState(defaultValues.pageNumber);

  /**
   * Updates filters and resets page number.
   * - If the authStatus filter is cleared, sets it to 'All' by default.
   * - Always resets pagination to the first page when filters change.
   *
   * This ensures that the table view is consistent with the new filter state and avoids empty pages.
   */
  const updateFilters = useCallback(
    (filterType: Filter, filterValues: string[]) => {
      // If the authStatus filter is empty, set it to "All" as default
      if (filterType === "authStatus" && filterValues.length === 0) {
        filterValues = ["All"];
      }

      setFilters({
        ...filters,
        [filterType]: filterValues,
      });
      setPageNumber(1);
    },
    [filters],
  );

  /**
   * Memoizes the context value to prevent unnecessary re-renders in consumers.
   * Only updates when relevant state changes.
   */
  const values = useMemo(
    () => ({
      sortCriteria,
      sortOrder,
      filters,
      pageNumber,
      updateFilters,
      setPageNumber,
      setSortOrder,
      setSortCriteria,
    }),
    [
      sortCriteria,
      sortOrder,
      filters,
      pageNumber,
      updateFilters,
      setPageNumber,
      setSortOrder,
      setSortCriteria,
    ],
  );

  return (
    <OrdersContext.Provider value={values}>{children}</OrdersContext.Provider>
  );
};

/**
 * useOrdersContext hook for accessing order table state and actions.
 */
export const useOrdersContext = () => useContext(OrdersContext);
